import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

// Import pages
import Home from '../../pages/Home/Home';
import Faculty from '../../components/Faculty/Faculty';
import Testimonials from '../../pages/Testimonials/Testimonials';
import Login from '../../pages/Members/Login';
import SignUp from '../../pages/Members/SignUp';
import Contact24 from '../../pages/Members/Contact24';
import Policies from '../../pages/Policies/Policies';
import PrivacyPolicy from '../../pages/PrivacyPolicy/PrivacyPolicy';
import FAQ from '../../pages/FAQ/FAQ';
import AboutUs from '../../components/AboutUs/AboutUs';
import Requests from '../../pages/Members/Requests';
import SummerWorkshops from '../../pages/Summer/SummerWorkshops';
import WorkshopDetail from '../../pages/Summer/WorkshopDetail';
import LessonPacks from '../../pages/Lessons/LessonPacks';
import RegisterStudent from '../../pages/Register/RegisterStudent';
import ChangeTime from '../../pages/ChangeTime/ChangeTime';
import ReminderStop from '../../pages/ReminderStop/ReminderStop';
import TrackLink from '../../pages/TrackLink/TrackLink';
import Fall from '../../pages/Fall/Fall';
import RoleBasedRoute from '../Auth/RoleBasedRoute';
import PortalRedirect from './PortalRedirect';
import PortalSpaceEntry from './PortalSpaceEntry';
import SpaceShell from '../Portal/SpaceShell';
// Phase E3a — role-rendered staff Work shell (admin dashboard / teacher shell).
import WorkShell, { WorkHome } from '../Portal/WorkShell';
import WorkTasksModule from '../Portal/Work/WorkTasksModule';

// Portal Auth Pages
import PortalLogin from '../../pages/Portal/PortalLogin';
import PortalSignup from '../../pages/Portal/PortalSignup';
import PortalClaim from '../../pages/Portal/PortalClaim';
import PortalForgotPassword from '../../pages/Portal/PortalForgotPassword';
import InstallInstructions from '../../pages/Portal/InstallInstructions';

// Customer Portal
import CustomerShell from '../Portal/CustomerShell';
import CustomerDashboard from '../../pages/CustomerPortal/CustomerDashboard';
import MyLessons from '../../pages/CustomerPortal/MyLessons';
import MakeupCalendar from '../../pages/CustomerPortal/MakeupCalendar';
import Payments from '../../pages/CustomerPortal/Payments';
import AccountSettings from '../../pages/CustomerPortal/AccountSettings';
import Family from '../../pages/CustomerPortal/Family';
import SeasonCalendar from '../../pages/CustomerPortal/SeasonCalendar';
import Songs from '../../pages/CustomerPortal/Songs';
import LessonRecaps from '../../pages/CustomerPortal/LessonRecaps';
import CustomerMore from '../../pages/CustomerPortal/More';
import AnnouncementsFeed from '../../pages/CustomerPortal/AnnouncementsFeed';
import PracticeLog from '../../pages/CustomerPortal/PracticeLog';

// Employee Portal (mounted under /portal/work via WorkShell — Phase E3a)
import Students from '../../pages/EmployeePortal/Students';
import StudentProfile from '../../pages/EmployeePortal/StudentProfile';
import MaterialRequests from '../../pages/EmployeePortal/MaterialRequests';
import AdminPanel from '../../pages/EmployeePortal/AdminPanel';
import Canon from '../../pages/EmployeePortal/Canon';
import Billing from '../../pages/EmployeePortal/Billing';
import StaffAccess from '../../pages/EmployeePortal/StaffAccess';
import ImportStudents from '../../pages/EmployeePortal/ImportStudents';
import TodayPage from '../../pages/EmployeePortal/TodayPage';
// Redesign (2026-07-22): the spine lives in LessonDashboard/index.js.
import LessonDashboard from '../../pages/EmployeePortal/LessonDashboard';
import StagePage from '../../pages/EmployeePortal/LessonDashboard/stage/StagePage';
import LyricsWindowPage from '../../pages/EmployeePortal/LessonDashboard/songpanel/LyricsWindowPage';
import ManageLessonsPage from '../../pages/EmployeePortal/ManageLessonsPage';
import ImportLessonsPage from '../../pages/EmployeePortal/ImportLessonsPage';
import LinkLessonsPage from '../../pages/EmployeePortal/LinkLessonsPage';
import SetupStudentTypes from '../../pages/EmployeePortal/SetupStudentTypes';
import Announcements from '../../pages/EmployeePortal/Announcements';
import EmailTemplates from '../../pages/EmployeePortal/EmailTemplates';
import EmailFamilies from '../../pages/EmployeePortal/EmailFamilies';
import ManageAnnouncements from '../../pages/EmployeePortal/ManageAnnouncements';
// RecitalSongs is DELIBERATELY NOT IMPORTED — unrouted 2026-08-30, file kept.
// It was a 2025 trial run for the YouTube API and was never implemented, but it
// shipped 46 real student names and its own plaintext password into the PUBLIC
// bundle: an import is what puts a module in the bundle, and the bundle is a
// static file served to anyone. The route's auth guard gates what RENDERS, never
// what SHIPS. Restoring the import is what re-publishes it, so evaluate the
// feature (portal card) before adding this line back.
import RecitalPlanning from '../../pages/EmployeePortal/RecitalPlanning/RecitalPlanning';
// TeacherTasksPage (old /portal/employee/tasks list) retired in Phase C — the
// task surface is now the Work module at /portal/work/tasks.
// NotesPage is the read-only browse over `documents` — i.e. LESSON RECAPS.
// Distinct from Docs (workDocs) below; renamed in the UI to stop the collision.
import NotesPage from '../../pages/EmployeePortal/NotesPage';
// Docs — the in-portal Notion replacement (workDocs, Work space, staff-only).
import DocsList from '../../pages/EmployeePortal/Docs/DocsList';
import DocEditor from '../../pages/EmployeePortal/Docs/DocEditor';
import TeacherScheduling from '../../pages/EmployeePortal/TeacherScheduling';
import SchedulePrint from '../../pages/EmployeePortal/SchedulePrint';
import LessonsTaught from '../../pages/EmployeePortal/LessonsTaught/LessonsTaught';
import WorkshopEnrollments from '../../pages/EmployeePortal/WorkshopEnrollments';
import LessonPackPurchases from '../../pages/EmployeePortal/LessonPackPurchases';
import Inquiries from '../../pages/EmployeePortal/Inquiries';
import Scheduling from '../../pages/EmployeePortal/Scheduling';
import MySchedule from '../../pages/EmployeePortal/MySchedule';
import WarmupsPage from '../../pages/EmployeePortal/Warmups/WarmupsPage';
import NotificationsPage from '../../pages/EmployeePortal/NotificationsPage';
import HelpPage from '../Portal/Guide/HelpPage';

// Phase E3a back-compat: every former /portal/employee path maps 1:1 to its
// /portal/work home (bookmarks, deep links, the current PWA employee shortcut).
// Prefix-swap preserves the sub-path + query string. The destination routes
// carry the role guard, so unauthed hits still fall through to login.
function EmployeeToWorkRedirect() {
	const location = useLocation();
	const target =
		location.pathname.replace(/^\/portal\/employee/, '/portal/work') +
		location.search;
	return <Navigate to={target} replace />;
}

function AnimatedRoutes() {
	const location = useLocation();

	return (
		<AnimatePresence mode='wait'>
			<motion.div
				initial={{ opacity: 0, y: 10 }}
				animate={{ opacity: 1, y: 0 }}
				exit={{ opacity: 0, y: -10 }}
				transition={{ duration: 0.3 }}
				style={{ minHeight: '100vh', width: '100%' }} // Ensure full page rendering
			>
				<Routes location={location} key={location.pathname}>
					{/* Public Routes */}
					<Route path='/' element={<Home />} />
					<Route path='/about' element={<AboutUs />} />
					<Route path='/testimonials' element={<Testimonials />} />
					<Route path='/sign-up' element={<SignUp />} />
					<Route path='/login' element={<Login />} />
					<Route path='/terms' element={<Policies />} />
					<Route path='/policies' element={<Policies />} />
					<Route path='/privacy' element={<PrivacyPolicy />} />
					<Route path='/faq' element={<FAQ />} />
					<Route path='/contact' element={<Contact24 />} />
					<Route path='/faculty' element={<Faculty />} />
					<Route path='/register' element={<RegisterStudent />} />
					{/* Token-keyed lesson-time change form for an existing family
					    (functions/slotChange.js). Public because the parent may not
					    have claimed a portal account; noindex via noindexRoutes. */}
					<Route path='/change-time' element={<ChangeTime />} />
					{/* The "Stop them here" link in a lesson reminder
					    (functions/lessonReminders.js). Public: a student or parent
					    may have no portal login; the token in the link is the key.
					    Opening it changes nothing; the button does. noindex via
					    noindexRoutes. */}
					<Route path='/reminders/stop' element={<ReminderStop />} />
					{/* A family's track link from an email: /t/<storage path>?k=<token>
					    plays one library cut and offers the download, so the email
					    links coloryourvoice.com and never names the Storage host
					    (src/services/trackLinks.js). Public: the file's own download
					    token is the only key; noindex via noindexRoutes. */}
					<Route path='/t/*' element={<TrackLink />} />
					{/* Season information for returning families — the destination
					    the welcome email links to. Public and INDEXABLE on purpose:
					    families cannot reach the portal until they claim an account,
					    so this cannot live behind auth. Deliberately NOT added to
					    App.js's isNoindexRoute set. */}
					<Route path='/fall' element={<Fall />} />
					{/* /trial — the Aug 2026 free-trial booking page — was DELETED on
					    2026-09-03 with its trialSlots collection and callables (card
					    PZ9bHlQONFvFRKVgGlH9). Trials are booked by hand until Anthony
					    reopens them publicly; reopening is a revert of that merge. */}
					<Route path='/requests' element={<Requests />} />
						<Route path='/summer' element={<SummerWorkshops />} />
						<Route
							path='/summer/:workshopId'
							element={<WorkshopDetail />}
						/>
						<Route path='/lessons' element={<LessonPacks />} />

					{/* Portal Auth Routes (New System) */}
					<Route path='/portal/login' element={<PortalLogin />} />
					<Route path='/portal/signup' element={<PortalSignup />} />
					{/* Enrollment-gated account claim (§3g): public route, the
						emailed claim link lands here with ?token=… */}
					<Route path='/portal/claim' element={<PortalClaim />} />
					<Route
						path='/portal/forgot-password'
						element={<PortalForgotPassword />}
					/>
					<Route
						path='/portal/install'
						element={<InstallInstructions />}
					/>
					{/* Convenience redirects */}
					<Route path='/employee/login' element={<PortalLogin />} />
					<Route path='/customer/login' element={<PortalLogin />} />

					{/* Customer Portal Routes — mobile-first shell (CustomerShell:
					    PortalSpaceChrome + bottom tab bar) wrapping the nested customer
					    pages. Routes are renamed to semantic paths matching the nav;
					    the live pre-shell paths redirect so bookmarks/claim links
					    survive. Practice Log stays in the tree but is intentionally
					    UNLINKED (no nav entry) in v1. */}
					<Route
						element={
							<RoleBasedRoute
								allowedRoles={['customer', 'student']}
							/>
						}>
						<Route path='/portal/customer' element={<CustomerShell />}>
							{/* Home hub. */}
							<Route index element={<CustomerDashboard />} />

							{/* Schedule — the re-homed MyLessons (Slice B/C behavior,
							    family-wide makeup gating). Live /lessons +
							    /manage-lessons redirect here so old links survive. */}
							<Route path='schedule' element={<MyLessons />} />
							<Route
								path='lessons'
								element={
									<Navigate to='/portal/customer/schedule' replace />
								}
							/>
							<Route
								path='manage-lessons'
								element={
									<Navigate to='/portal/customer/schedule' replace />
								}
							/>

							{/* Songs — reserved empty state (notes read surface deferred). */}
							<Route path='songs' element={<Songs />} />

							{/* Lesson Recaps — every shared recap, per child (design pass 2,
							    change 12). Reached from Home and More. Open to a student
							    principal too: the query narrows to the tier its rule admits. */}
							<Route path='recaps' element={<LessonRecaps />} />

							{/* More menu + its secondary destinations. */}
							<Route path='more' element={<CustomerMore />} />
							<Route path='account' element={<AccountSettings />} />

							{/* GUARDIAN-ONLY, nested guard — the same pattern the
							    admin-only staff tools use below, so a student cannot
							    reach these by URL. Money and family composition are a
							    payer's business; redeeming a makeup is a guardian act,
							    which portalClaims.js states where it refuses to mint a
							    student account under an unclaimed family ("no adult to
							    call, and cancel/redeem are guardian-only acts"). A
							    student landing here gets the standard Access Denied
							    rather than a page that renders empty and reads broken. */}
							<Route
								element={
									<RoleBasedRoute
										allowedRoles={['customer']}
									/>
								}>
								<Route
									path='payments'
									element={<Payments />}
								/>
								<Route path='family' element={<Family />} />
								{/* Makeup redeem — reached from Schedule, not a tab. */}
								<Route
									path='makeup'
									element={<MakeupCalendar />}
								/>
							</Route>
							<Route
								path='announcements'
								element={<AnnouncementsFeed />}
							/>
							<Route path='calendar' element={<SeasonCalendar />} />

							{/* Kept but UNLINKED in v1 (no tab / More entry). */}
							<Route
								path='practice-log'
								element={<PracticeLog />}
							/>

							{/* Undefined /portal/customer/* -> the Home hub. */}
							<Route
								path='*'
								element={
									<Navigate to='/portal/customer' replace />
								}
							/>
						</Route>
					</Route>

					{/* The room's second screen (Lesson Dashboard pass 3, release 5):
					    a window on the room Mac, dragged to the spare monitor once and
					    left there. Staff-only, but OUTSIDE /portal/work on purpose: it
					    is full-bleed stage content with no portal chrome, and it must
					    survive the lesson tab moving between lessons. It renders only
					    what the lesson sends over the BroadcastChannel (stage/StagePage.js);
					    App.js hides the site chrome and noindexRoutes covers it. */}
					<Route
						element={
							<RoleBasedRoute
								allowedRoles={['employee', 'admin']}
							/>
						}>
						<Route path='/stage' element={<StagePage />} />
						{/* The lyrics column in its own window (2026-10-05): the lesson
						    tab portals its LyricsPane into this page's mount node
						    (components/PopoutWindow.js). Same shape as /stage: staff-only,
						    outside the portal chrome, noindex. */}
						<Route path='/lyrics-window' element={<LyricsWindowPage />} />
					</Route>

					{/* Phase E3a — the staff dashboard is canonical at /portal/work.
					    WorkShell role-renders the CHROME: admin → PortalLayout (the
					    grouped admin sidebar + tools); non-admin employee → a Phase B
					    PortalSpaceChrome single-space placeholder (the real teacher
					    dashboard lands in E3b). Both render <Outlet/>, so the nested
					    admin pages below resolve into whichever chrome the role picks.
					    Admin-only setup/data tools keep a nested admin guard so a
					    non-admin can't reach them by URL. */}
					<Route
						element={
							<RoleBasedRoute
								allowedRoles={['employee', 'admin']}
							/>
						}>
						<Route path='/portal/work' element={<WorkShell />}>
							{/* Role-rendered home: admin dashboard / teacher shell. */}
							<Route index element={<WorkHome />} />

							{/* Daily-core. */}
							<Route path='today' element={<TodayPage />} />
							<Route
								path='lesson/:lessonId'
								element={<LessonDashboard />}
							/>
							<Route path='students' element={<Students />} />
							<Route
								path='students/:studentId'
								element={<StudentProfile />}
							/>
							{/* Warmups — the studio's warmup catalog and the surface Grace
							    reviews it on. Inside the employee|admin block, so every
							    teacher reaches it and no family account can; the real wall
							    is firestore.rules gating warmupExercises on
							    isEmployeeOrAdmin(). Deliberately NOT admin-gated: this is
							    teaching material, not staffing or billing data. */}
							<Route path='warmups' element={<WarmupsPage />} />
							{/* Notifications — every staff push, persisted for 90 days
							    (notification center, 2026-09-22). Every staff role: each
							    person sees only their own, which the rule enforces. */}
							<Route path='notifications' element={<NotificationsPage />} />
							{/* The staff guide (How do I…): every staff role. */}
							<Route path='help' element={<HelpPage />} />
							<Route path='scheduling' element={<Scheduling />} />
							{/* Legacy timeSlots deep links -> unified Scheduling
							    surface (tab preselected). Preserves bookmarks. */}
							<Route
								path='schedule'
								element={
									<Navigate
										to='/portal/work/scheduling?tab=calendar'
										replace
									/>
								}
							/>
							<Route
								path='manage-time-slots'
								element={
									<Navigate
										to='/portal/work/scheduling?tab=editor'
										replace
									/>
								}
							/>
							<Route
								path='generate-time-slots'
								element={
									<Navigate
										to='/portal/work/scheduling?tab=editor'
										replace
									/>
								}
							/>
							{/* A teacher's OWN assignments. Lives beside the studio
							    board rather than inside it: the board is an admin
							    tool, this is the page any teaching artist opens to
							    see their week. */}
							<Route path='my-schedule' element={<MySchedule />} />
							{/* Teacher Scheduling — ADMIN ONLY at the route (Anthony,
							    2026-08-30). It sat inside the employee+admin block with
							    no gate, and every isAdmin check inside the page guards a
							    WRITE, not rendering — so a teacher reaching it by URL
							    could not change anything but could read the whole rail:
							    every teacher's block/lesson load, all coverage requests,
							    and each teacher's lesson CAP. Annelise is the only capped
							    teacher (maxLessons 30; the two owners are uncapped), so
							    that panel reads as being about her specifically.

							    Nothing legitimate is lost. Teachers get a studio-wide
							    view from My Schedule -> Full studio, and they request
							    coverage from their own dashboard; the board is where an
							    admin DECIDES on those requests.

							    UI-LEVEL ONLY, deliberately. firestore.rules still allows
							    `teachers` reads to anyone with viewSchedule, because
							    CalendarTab's instructor picker needs that collection — so
							    maxLessons stays readable to a determined person with
							    devtools. Accepted by Anthony 2026-08-30 on the grounds
							    that the UI is the practical surface; moving caps out of
							    the client-readable doc is a separate decision. */}
							<Route
								element={
									<RoleBasedRoute allowedRoles={['admin']} />
								}>
								<Route
									path='teacher-scheduling'
									element={<TeacherScheduling />}
								/>
								{/* A printable copy of any run, draft or released.
								    Admin-only with the board it comes from. */}
								<Route
									path='teacher-scheduling/print'
									element={<SchedulePrint />}
								/>
								{/* Per-teacher monthly lesson counts — the pay record.
								    Admin-only for the same reason as the board above,
								    and a sharper one: it shows every teacher's count. */}
								<Route
									path='lessons-taught'
									element={<LessonsTaught />}
								/>
							</Route>
							<Route
								path='workshop-enrollments'
								element={<WorkshopEnrollments />}
							/>
							<Route
								path='lesson-packs'
								element={<LessonPackPurchases />}
							/>
							<Route path='inquiries' element={<Inquiries />} />
							<Route
								path='manage-lessons'
								element={<ManageLessonsPage />}
							/>
							<Route
								path='announcements'
								element={<Announcements />}
							/>
							<Route
								path='email-templates'
								element={<EmailTemplates />}
							/>
							{/* sendEmail-gated inside the page (admins/owners bypass);
							    route stays under the employee/admin guard. */}
							<Route
								path='email-families'
								element={<EmailFamilies />}
							/>
							<Route
								path='manage-announcements'
								element={<ManageAnnouncements />}
							/>
							<Route
								path='requests'
								element={<MaterialRequests />}
							/>
							<Route path='notes' element={<NotesPage />} />
							{/* Docs — bookmarkable landing surface + per-doc editor. */}
							<Route path='docs' element={<DocsList />} />
							<Route path='docs/:docId' element={<DocEditor />} />
							<Route
								path='recital-planning'
								element={<RecitalPlanning />}
							/>
							{/* recital-songs: route removed 2026-08-30 with its import
							    above. The page still exists in the repo and is not
							    deleted — see the import comment for why it is not
							    wired up. */}
							{/* Phase C task + project module, folded in as the
							    canonical Tasks page. */}
							<Route
								path='tasks'
								element={
									<WorkTasksModule isSingleSpace={false} />
								}
							/>

							{/* Setup & Data — ADMIN ONLY (route-level gate; the
							    sidebar/dashboard already hide these for non-admins). */}
							<Route
								element={
									<RoleBasedRoute allowedRoles={['admin']} />
								}>
								<Route
									path='import'
									element={<ImportStudents />}
								/>
								<Route
									path='import-lessons'
									element={<ImportLessonsPage />}
								/>
								<Route
									path='link-lessons'
									element={<LinkLessonsPage />}
								/>
								<Route
									path='setup-student-types'
									element={<SetupStudentTypes />}
								/>
								<Route path='admin' element={<AdminPanel />} />
								{/* Canon — route gate is admin (the coarsest guard the
								    router has); the OWNER wall is firestore.rules'
								    isOwner() on canonDocs, with the page rendering an
								    explanation rather than an error for a non-owner. */}
								<Route path='canon' element={<Canon />} />
								<Route
									path='staff-access'
									element={<StaffAccess />}
								/>
								{/* Billing is admin-only at the route AND on the
								    server — every callable behind it re-checks
								    staff, because requireBillingCaller also
								    admits a claimed customer for their own
								    family (right for the card flow, wrong here). */}
								<Route path='billing' element={<Billing />} />
							</Route>

							{/* Undefined /portal/work/* -> the dashboard home. */}
							<Route
								path='*'
								element={
									<Navigate to='/portal/work' replace />
								}
							/>
						</Route>
					</Route>

					{/* Back-compat: every old /portal/employee path -> its
					    /portal/work home (bookmarks, deep links, current PWA
					    shortcut). The staff front-door flip to /portal is E3b. */}
					<Route
						path='/portal/employee'
						element={<EmployeeToWorkRedirect />}
					/>
					<Route
						path='/portal/employee/*'
						element={<EmployeeToWorkRedirect />}
					/>

					{/* Portal multi-space hub + private space shells (Phase B).
					    Owners get the /portal hub (four space cards) and a
					    persistent space-switcher; a single-space user (employee,
					    e.g. Annelise -> Work) is redirected straight into their
					    dashboard. The Work space is now served by WorkShell above;
					    the three private spaces keep their Phase B SpaceShell. The
					    private routes carry a courtesy client guard inside
					    SpaceShell; the real guard is the Phase A Firestore rules. */}
					{/* /portal is role-tolerant (PortalSpaceEntry self-guards):
					    unauthed -> login, customer -> customer dashboard, owner ->
					    hub, single-space employee -> their dashboard. */}
					<Route path='/portal' element={<PortalSpaceEntry />} />
					<Route
						element={
							<RoleBasedRoute allowedRoles={['employee', 'admin']} />
						}>
						<Route
							path='/portal/personal'
							element={<SpaceShell spaceKey='personal' />}
						/>
						<Route
							path='/portal/owners'
							element={<SpaceShell spaceKey='owners' />}
						/>
						<Route
							path='/portal/barbara'
							element={<SpaceShell spaceKey='barbara' />}
						/>
						<Route
							path='/portal/bible'
							element={<SpaceShell spaceKey='bible' />}
						/>
					</Route>

					{/* Broad portal catch-all: any other undefined /portal/* path
					    -> login (unauthed) or role dashboard (authed). Defined
					    routes above always win; this only catches misses. */}
					<Route path='/portal/*' element={<PortalRedirect />} />

				</Routes>
			</motion.div>
		</AnimatePresence>
	);
}

export default AnimatedRoutes;
